import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db, signInAnonymouslyForField } from "../firebase";
import { S, C } from "../ui";
import { clearStaffSession } from "./StaffAuthScreen";
import AssetPicker from "../core/AssetPicker";
import AssetCheckIn from "../core/AssetCheckIn";
import MessageThread from "./MessageThread";
import { StaffWorkOrdersTab } from "./WorkOrdersPanel";
import ScrollHint from "../core/ScrollHint";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function dateLabel(dateStr) {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// The staff shell for anyone who logged in via StaffAuthScreen.jsx
// (facility code + name + PIN) rather than a QR scan — Phase 11 gives
// this just the "Check In" tab (pick an asset, then the same
// AssetCheckIn.jsx a QR scan uses); Messages (Phase 12) and My Schedule
// (Phase 13, read-only — same schedules query/rendering shape as
// EntityDetailPanel.jsx's worker view, just without needing a
// facilityId+idField feed/message setup this shell has no other use for)
// are wired up here too.
export default function StaffHome({ facilityId, workerId }) {
  const [ready, setReady] = useState(false);
  const [worker, setWorker] = useState(undefined); // undefined = loading, null = missing
  const [tab, setTab] = useState("checkin");
  const [pickedAsset, setPickedAsset] = useState(null);
  const [schedules, setSchedules] = useState([]);

  useEffect(() => { signInAnonymouslyForField().then(() => setReady(true)); }, []);
  useEffect(() => {
    if (!ready) return;
    return onSnapshot(doc(db, "workers", workerId), s => setWorker(s.exists() ? { id: s.id, ...s.data() } : null));
  }, [ready, workerId]);
  useEffect(() => {
    if (!ready) return;
    return onSnapshot(query(collection(db, "schedules"), where("facilityId", "==", facilityId), where("scopeType", "==", "worker"), where("scopeId", "==", workerId)), s =>
      setSchedules(s.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [ready, facilityId, workerId]);

  if (!ready || worker === undefined) return <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>Loading…</div>;
  if (worker === null) {
    return (
      <div style={S.center}><div style={{ ...S.card, textAlign: "center" }}>
        <div style={S.title}>You've been removed from this facility</div>
        <div style={S.sub}>Ask your admin if this seems wrong.</div>
        <button style={S.btn(C.navy)} onClick={() => { clearStaffSession(); window.location.reload(); }}>Sign Out</button>
      </div></div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header} className="app-header">
        <div>
          <div style={{ fontWeight: 700 }}>{worker.name}</div>
          <div style={{ fontSize: 12, color: "#94A3B8" }}>{worker.trade}</div>
        </div>
        <button style={{ ...S.ghostBtn, width: "auto", padding: "8px 14px", color: "#A8C0E8", borderColor: "#3A5275" }}
          onClick={() => { clearStaffSession(); window.location.reload(); }}>Sign Out</button>
      </div>

      <ScrollHint className="app-tabbar" style={{ display: "flex", gap: 4, padding: "0 24px", background: C.white, borderBottom: `1px solid ${C.border}` }} fadeColor={C.white}>
        {[["checkin", "Check In"], ["workorders", "Work Orders"], ["messages", "Messages"], ["schedule", "My Schedule"]].map(([id, label]) => (
          <div key={id} onClick={() => setTab(id)}
            style={{ padding: "12px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: tab === id ? C.accent : C.muted, borderBottom: tab === id ? `2px solid ${C.accent}` : "2px solid transparent" }}>
            {label}
          </div>
        ))}
      </ScrollHint>

      <div style={S.body} className="app-body">
        {tab === "checkin" && (
          pickedAsset
            ? (<>
                <div onClick={() => setPickedAsset(null)} style={{ fontSize: 12, color: C.accent, cursor: "pointer", marginBottom: 16 }}>← Choose a different asset</div>
                <AssetCheckIn facilityId={facilityId} assetId={pickedAsset.id} worker={worker} />
              </>)
            : <AssetPicker facilityId={facilityId} trade={worker.trade} onPick={setPickedAsset} />
        )}
        {tab === "workorders" && <StaffWorkOrdersTab worker={worker} />}
        {tab === "messages" && (
          <MessageThread facilityId={facilityId} threadType="worker" threadId={worker.id} senderRole="worker" senderName={worker.name} />
        )}
        {tab === "schedule" && (
          // Pending vendor-proposed entries aren't confirmed yet — don't
          // show them as this worker's real schedule until a Facility
          // Admin approves (see ScheduleEditor.jsx).
          schedules.filter(s => s.status !== "pending").length === 0 ? (
            <div style={{ color: C.muted, fontSize: 13 }}>No schedule set.</div>
          ) : (
            schedules.filter(s => s.status !== "pending").map(s => (
              <div key={s.id} style={{ fontSize: 13, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                {s.label && <b>{s.label} — </b>}
                {s.date ? dateLabel(s.date) : s.daysOfWeek.map(d => DAY_LABELS[d]).join(", ")}, {s.startTime}–{s.endTime}
                {s.date && <span style={{ ...S.pill("#EFF6FF", C.accent), marginLeft: 8 }}>One-time</span>}
              </div>
            ))
          )
        )}
      </div>
    </div>
  );
}
